import { useCallback, useEffect, useState, type JSX } from 'react';
import { toast } from 'sonner';
import { Form, FormActions, FormMessage } from '@/components/form';
import { PillButton } from '@/components/entity/PillButton';
import { UnitDeleteButton } from '@/components/dashboard/units/UnitDeleteButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useUnitUpdateMutation } from '@/hooks/useDashboardProperties';
import { unitFormSchema } from '@/lib/validation/property';
import type { UnitSummary } from '@/lib/types/property';
import { UnitFormFields } from '@/routes/dashboard/unitForm/UnitFormFields';
import { formatUnitLabel } from '@/lib/utils/propertyFormatting';
import {
  buildUnitPayload,
  createUnitInitialState,
  mapUnitIssues,
  type UnitFormErrors,
  type UnitFormState,
} from '@/routes/dashboard/unitForm/unitFormState';

export interface UnitEditFormProps {
  propertyId: string;
  unit: UnitSummary;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export function UnitEditForm({
  propertyId,
  unit,
  onCancel,
  onSaved,
  onDeleted,
}: UnitEditFormProps): JSX.Element {
  const user = useDashboardUser();
  const updateMutation = useUnitUpdateMutation({ user, propertyId });
  const [formState, setFormState] = useState<UnitFormState>(() => createUnitInitialState(unit));
  const [errors, setErrors] = useState<UnitFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isUploadingHero, setIsUploadingHero] = useState(false);

  const clearFieldError = useCallback((field: keyof UnitFormState) => {
    setErrors((current) => {
      if (current[field] === undefined) {
        return current;
      }
      const { [field]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const disabled = updateMutation.isPending;

  const handleSubmit = useCallback(() => {
    if (updateMutation.isPending) {
      return;
    }
    setFormError(null);
    setErrors({});

    if (isUploadingHero) {
      setFormError('Wait for the hero image upload to finish.');
      return;
    }

    const payload = buildUnitPayload({ propertyId, state: formState, unit });
    const validation = unitFormSchema.safeParse(payload);

    if (!validation.success) {
      setErrors(mapUnitIssues(validation.error.issues));
      const firstMessage = validation.error.issues.at(0)?.message ?? 'Fix the highlighted fields.';
      setFormError(firstMessage);
      return;
    }

    updateMutation.mutate(
      { unit, values: validation.data },
      {
        onSuccess: () => {
          toast.success(`${formatUnitLabel(validation.data)} updated.`);
          onSaved();
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Unable to save unit.';
          setFormError(message);
          toast.error('Unable to save unit.', { description: message });
        },
      },
    );
  }, [formState, isUploadingHero, onSaved, propertyId, unit, updateMutation]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.key || event.key.toLowerCase() !== 's') {
        return;
      }
      if (event.altKey || !(event.metaKey || event.ctrlKey) || event.repeat) {
        return;
      }
      event.preventDefault();
      handleSubmit();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit]);

  return (
    <Form
      gap="lg"
      onSubmit={(event) => {
        if (!event.currentTarget.reportValidity()) {
          return;
        }
        event.preventDefault();
        handleSubmit();
      }}
    >
      {formError !== null ? (
        <FormMessage tone="error" role="alert">
          {formError}
        </FormMessage>
      ) : null}

      <UnitFormFields
        propertyId={propertyId}
        formState={formState}
        errors={errors}
        disabled={disabled}
        isUploadingHero={isUploadingHero}
        setFormState={setFormState}
        clearFieldError={clearFieldError}
        onUploadingHeroChange={setIsUploadingHero}
      />

      <div
        className="sticky bottom-0 z-10 -mx-6 border-t border-surface-subtle bg-white px-6 pt-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        data-testid="dashboard-save-bar"
      >
        <FormActions justify="between">
          <UnitDeleteButton
            propertyId={propertyId}
            unit={unit}
            variant="secondary"
            onDeleted={onDeleted}
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/70 transition hover:bg-surface-subtle disabled:opacity-60"
              onClick={onCancel}
              disabled={disabled || isUploadingHero}
            >
              Cancel
            </button>
            <PillButton type="submit" disabled={disabled || isUploadingHero}>
              {disabled ? 'Saving…' : 'Save changes'}
            </PillButton>
          </div>
        </FormActions>
      </div>
    </Form>
  );
}
